import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import {
  Shield,
  FileText,
  PenTool,
  CheckCircle2,
  Clock,
  AlertCircle,
  Loader2,
  Users,
  Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExternalSigningPage = () => {
  const { contractId, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [signingData, setSigningData] = useState(null);
  const [error, setError] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  
  // Signature canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    fetchSigningData();
  }, [contractId, token]);

  useEffect(() => {
    if (signingData?.can_sign && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, [signingData]);

  const fetchSigningData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/sign/${contractId}/${token}`);
      setSigningData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Enlace de firma inválido o expirado');
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing functions
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const handleSign = async () => {
    const canvas = canvasRef.current;
    const signatureImage = canvas.toDataURL('image/png');
    
    // Check if signature is empty
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    let hasSignature = false;
    for (let i = 0; i < pixels.length; i += 4) {
      if (pixels[i] !== 15 || pixels[i + 1] !== 23 || pixels[i + 2] !== 42) {
        hasSignature = true;
        break;
      }
    }
    
    if (!hasSignature) {
      toast.error('Por favor, dibuja tu firma antes de continuar');
      return;
    }

    setSigning(true);
    try {
      const response = await axios.post(`${API_URL}/api/sign/${contractId}/${token}`, {
        signer_name: signingData.signer_name,
        signature_image: signatureImage,
        signature_type: 'manual'
      });
      
      setSigned(true);
      toast.success('¡Documento firmado exitosamente!');
      
      // Show completion status
      if (response.data.all_complete) {
        toast.success('¡Todas las firmas han sido completadas!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al firmar el documento');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-slate-400">Cargando documento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-red-500/30 rounded-xl p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-white mb-2">Enlace Inválido</h1>
          <p className="text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-emerald-500/30 rounded-xl p-8 max-w-md text-center">
          <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-white mb-2">¡Firma Completada!</h1>
          <p className="text-slate-400 mb-4">Has firmado exitosamente el documento</p>
          <p className="text-sm text-slate-500">"{signingData.contract_title}"</p>
        </div>
      </div>
    );
  }

  if (!signingData.can_sign) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-amber-500/30 rounded-xl p-8 max-w-md text-center">
          <Clock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-white mb-2">Esperando Firmas Previas</h1>
          <p className="text-slate-400 mb-4">{signingData.message}</p>
          <p className="text-sm text-slate-500">Documento: "{signingData.contract_title}"</p>
          <button
            onClick={fetchSigningData}
            className="mt-4 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
          >
            Verificar Estado
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 py-8 px-4">
      <Toaster position="top-center" richColors />
      
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="w-8 h-8 text-blue-500" />
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              NotaryOS
            </span>
          </div>
          <h1 className="text-xl font-semibold text-white">Firma de Documento</h1>
          <p className="text-slate-400 mt-1">Hola {signingData.signer_name}, has sido invitado/a a firmar</p>
        </div>

        {/* Contract Preview */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden mb-6">
          <div className="p-4 border-b border-slate-700 bg-slate-800/50">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              <h2 className="font-semibold">{signingData.contract_title}</h2>
            </div>
          </div>
          <div className="p-4 max-h-64 overflow-y-auto">
            <div className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap">
              {signingData.contract_content}
            </div>
          </div>
        </div>

        {/* Signers Status */}
        {signingData.all_signers && signingData.all_signers.length > 1 && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-5 h-5 text-purple-400" />
              <h3 className="font-medium">Estado de Firmas</h3>
            </div>
            <div className="space-y-2">
              {signingData.all_signers.map((signer, index) => (
                <div 
                  key={index}
                  className={`flex items-center justify-between p-2 rounded-lg ${
                    signer.name === signingData.signer_name 
                      ? 'bg-blue-500/10 border border-blue-500/30' 
                      : 'bg-slate-700/50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 flex items-center justify-center bg-slate-600 rounded-full text-xs">
                      {signer.order || index + 1}
                    </span>
                    <span className="text-sm">
                      {signer.name}
                      {signer.name === signingData.signer_name && (
                        <span className="text-blue-400 ml-1">(Tú)</span>
                      )}
                    </span>
                  </div>
                  {signer.status === 'signed' ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <Clock className="w-5 h-5 text-amber-400" />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Signature Canvas */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <PenTool className="w-5 h-5 text-blue-400" />
              <h3 className="font-medium">Tu Firma</h3>
            </div>
            <button
              onClick={clearSignature}
              className="flex items-center gap-1 text-sm text-slate-400 hover:text-white"
            >
              <Trash2 className="w-4 h-4" />
              Limpiar
            </button>
          </div>
          
          <canvas
            ref={canvasRef}
            width={600}
            height={200}
            className="w-full border border-slate-600 rounded-lg cursor-crosshair touch-none"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            data-testid="signature-canvas"
          />
          
          <p className="text-xs text-slate-400 mt-2 text-center">
            Dibuja tu firma arriba usando el mouse o tu dedo
          </p>

          <button
            onClick={handleSign}
            disabled={signing}
            data-testid="submit-signature-btn"
            className="w-full mt-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 disabled:opacity-50 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
          >
            {signing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Firmando...
              </>
            ) : (
              <>
                <PenTool className="w-5 h-5" />
                Firmar Documento
              </>
            )}
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-xs text-slate-500">
          <p>Documento protegido por NotaryOS</p>
          <p>Tu firma digital tiene validez legal</p>
        </div>
      </div>
    </div>
  );
};

export default ExternalSigningPage;
